import { useMemo, useRef, useState, useEffect, useCallback, FunctionComponent } from 'react';
import moment from 'moment';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js';

import { FormItemName, Input, RadioButton, Select, FormError } from '../../components/ui';
import { InlineSpinner } from '../../components/common/spinner';
import { DatePicker } from '../../components/date-picker';

import { useUser } from '../../hooks/api/useUser';
import { useProfileTagGroups, TagGroup } from '../../hooks/api/useProfileTagGroups';
import { useAppSettings, useUserStateOptions } from '../../hooks/api/useAppSettings';
import Toast from '../../components/common/pop-up-message';
import { generateDefaultValues, profileSchema, ProfileFormData } from '../../utils/profile-validation';
import { INVALID_DATE } from '../../utils/profile-validation';
import useAuth from '../../hooks/utils/useAuth';
import { useSendKeycloakVerificationCode, useVerifyKeycloakVerificationCode } from '../../hooks/api/useUser';
import { normalizePhoneNumber } from '../../utils/normalize-phone-input';
import { OtpVerification } from '../../components/common/otp-verification';
import { UnsavedChangesModal } from '../../components/modals/unsaved-changes-modal';
import { PageHeader } from '../../components/modals/settings-modal/page-header';
import { FormBlock } from '../../components/modals/settings-modal/form-block';
import { resolveTheme } from '../../themes';

export interface ProfileProps {
  showUnsavedChangesModal?: boolean;
  onCloseUnsavedChangesModal?: () => void;
  onDiscardUnsavedChangesModal?: () => void;
  onSuccessSubmitted?: () => void;
  onChange?: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  onSaveRequestReady?: (saveHandler: () => Promise<void>) => void;
  onSaveSuccess?: () => void;
}

export const Profile: FunctionComponent<ProfileProps> = ({
  showUnsavedChangesModal,
  onCloseUnsavedChangesModal,
  onDiscardUnsavedChangesModal,
  onSuccessSubmitted,
  onChange,
  onSubmittingChange,
  onSaveRequestReady,
  onSaveSuccess,
}) => {
  const toastNotificationContainerRef = useRef<HTMLDivElement>(null);
  const { user, isLoading: userIsLoading, getNewToken } = useAuth();
  const { updateUser } = useUser();
  const { data: formOptions, isLoading: isTagsLoading } = useProfileTagGroups();
  const { data: appSettings, isLoading: isAppLoading } = useAppSettings();
  const { data: userStateOptions, isLoading: isStateOptionsLoading } = useUserStateOptions();

  const [showPhoneValidation, setShowPhoneValidation] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [isPhoneValidating, setIsPhoneValidating] = useState(false);
  const [phoneValidationError, setPhoneValidationError] = useState<string | null>(null);
  const [otpValidationError, setOtpValidationError] = useState<string | null>(null);
  const [hasChangedPhoneNumber, setHasChangedPhoneNumber] = useState(false);

  const { mutate: sendKeycloakVerificationCode } = useSendKeycloakVerificationCode();
  const { mutate: verifyKeycloakVerificationCode } = useVerifyKeycloakVerificationCode();

  const loading = userIsLoading || isTagsLoading || isAppLoading || isStateOptionsLoading;
  const theme = resolveTheme();
  const groupId = user?.caller_role.active_subscription?.package.code || null;
  const preferNotToSay = 'I prefer not to say';

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isDirty, isSubmitting, isValid },
    reset,
    watch,
    getValues,
  } = useForm({
    defaultValues: useMemo(() => {
      const profileFields = ['gender', 'pronoun'];
      profileFields.forEach((field) => {
        if (user?.[field] === null) {
          user[field] = preferNotToSay;
        }
      });

      return generateDefaultValues(user);
    }, [user]),
    resolver: yupResolver(profileSchema(user, groupId)),
    mode: 'onBlur',
  });

  // Must match generateDefaultValues so restoring it leaves the form clean.
  const savedPhoneFormatted = user?.mobile_phone ? normalizePhoneNumber(user.mobile_phone.replace('+1', '')) : '';
  // Keyed off hasChangedPhoneNumber so clearing the field still counts as a
  // pending change rather than looking like "no change".
  const hasPendingPhoneChange = hasChangedPhoneNumber && phoneNumber !== savedPhoneFormatted;
  const hasUnvalidatedPhoneChange = hasPendingPhoneChange || showPhoneValidation;

  // Keep form in sync when auth user hydrates after mount (e.g. post-onboarding).
  // Matches ProfileStep / Notifications; defaultValues alone do not update RHF.
  // Skip while an in-progress phone change/OTP would be wiped by re-hydrating mobile_phone.
  useEffect(() => {
    if (!user || loading) return;
    if (showPhoneValidation || hasPendingPhoneChange) return;
    const profileFields = ['gender', 'pronoun'] as const;
    const nextUser = { ...user };
    profileFields.forEach((field) => {
      if (nextUser[field] === null) {
        nextUser[field] = preferNotToSay;
      }
    });
    reset(generateDefaultValues(nextUser));
    // Depend on field values, not user object identity (select() returns new refs).
  }, [
    user?.id,
    user?.first_name,
    user?.last_name,
    user?.email_address,
    user?.mobile_phone,
    user?.date_of_birth,
    user?.gender,
    user?.pronoun,
    user?.timezone,
    loading,
    reset,
    showPhoneValidation,
    hasPendingPhoneChange,
  ]);

  useEffect(() => {
    if (onSubmittingChange) {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);

  useEffect(() => {
    if (isDirty && onChange) {
      onChange();
    }
  }, [isDirty, onChange]);

  const supportedTimezonesOptions = useMemo(
    () =>
      appSettings?.transformed_timezones
        ? Object.entries(appSettings.transformed_timezones).map(([label, value]) => ({
            label,
            value,
          }))
        : [],
    [appSettings?.transformed_timezones],
  );

  const { genderOptionsList, pronounOptionsList } = useMemo(
    () => ({
      genderOptionsList: getFormOptions('GENDER', formOptions),
      pronounOptionsList: getFormOptions('PRONOUN', formOptions),
    }),
    [formOptions],
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasChangedPhoneNumber(true);
    const value = normalizePhoneNumber(e.target.value);
    setPhoneNumber(value);
    setPhoneValidationError(null);
    setShowPhoneValidation(false);
    // This input is driven by local state, so mirror it into the form to keep
    // Save enabled when the phone number is the only edit on the page.
    setValue('mobile_phone', value, { shouldDirty: true });
    if (onChange) onChange();
  };

  // Drops an in-progress phone edit and puts the saved number back in the form
  // so an unverified value can never be submitted.
  const discardPhoneChange = () => {
    setShowPhoneValidation(false);
    setOtpValue('');
    setOtpValidationError(null);
    setPhoneValidationError(null);
    setPhoneNumber('');
    setHasChangedPhoneNumber(false);
    setValue('mobile_phone', savedPhoneFormatted, { shouldDirty: false });
  };

  const handlePhoneValidation = async () => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length !== 10) {
      setPhoneValidationError('Please enter a valid 10-digit phone number');
      return;
    }

    setIsPhoneValidating(true);
    setPhoneValidationError(null);

    sendKeycloakVerificationCode(
      { mobilePhone: phoneNumber.replace(/\D/g, '') },
      {
        onSuccess: () => {
          Toast.success('Verification code sent successfully');
          setShowPhoneValidation(true);
          setIsPhoneValidating(false);
        },
        onError: (error: any) => {
          setPhoneValidationError(error.response?.data?.description || 'Failed to send verification code');
          setIsPhoneValidating(false);
        },
      },
    );
  };

  const handleOtpSubmit = async () => {
    if (otpValue.length !== 6) {
      setOtpValidationError('Please enter the 6-digit verification code');
      return;
    }

    setIsPhoneValidating(true);
    setOtpValidationError(null);

    verifyKeycloakVerificationCode(
      {
        verificationCode: otpValue,
      },
      {
        onSuccess: () => {
          Toast.success('Phone number verified successfully');
          setShowPhoneValidation(false);
          setOtpValue('');
          setValue('mobile_phone', phoneNumber, { shouldValidate: true, shouldDirty: false });
          setPhoneNumber('');
          setHasChangedPhoneNumber(false);
          setIsPhoneValidating(false);
          if (!isDirty && onSuccessSubmitted) {
            onSuccessSubmitted();
          }
        },
        onError: (error: any) => {
          setOtpValidationError(error.response?.data?.message || 'Invalid verification code. Please try again.');
          setOtpValue('');
          setIsPhoneValidating(false);
        },
      },
    );
  };

  const resendVerificationCode = () => {
    if (!phoneNumber) return;

    setIsPhoneValidating(true);
    setPhoneValidationError(null);

    sendKeycloakVerificationCode(
      { mobilePhone: phoneNumber.replace(/\D/g, '') },
      {
        onSuccess: () => {
          Toast.success('Verification code sent successfully');
          setIsPhoneValidating(false);
        },
        onError: (error: any) => {
          setPhoneValidationError(error.response?.data?.description || 'Failed to send verification code');
          setIsPhoneValidating(false);
        },
      },
    );
  };

  const submit: SubmitHandler<Partial<ProfileFormData>> = async (data) => {
    if (hasUnvalidatedPhoneChange) {
      setPhoneValidationError('Please verify your phone number before saving');
      throw new Error('Please verify your phone number before saving');
    }

    data.gender = data.gender === preferNotToSay ? null : data.gender;
    data.pronoun = data.pronoun === preferNotToSay ? null : data.pronoun;

    const { data: newUserData } = await updateUser.mutateAsync({
      ...user,
      first_name: data.first_name,
      last_name: data.last_name,
      gender: data.gender,
      pronoun: data.pronoun,
      street_address_1: data.street_address_1,
      street_address_2: data.street_address_2,
      city: data.city,
      state: data.state !== '' ? data.state : null,
      zip_code: data.zip_code,
      mobile_phone: data.mobile_phone,
      email_address: data.email_address,
      timezone: data.timezone,
      date_of_birth: data.date_of_birth,
      is_text_compatible_phone: data.is_text_compatible_phone,
    });

    reset(generateDefaultValues(newUserData));

    Toast.success('Profile updated successfully.', undefined, toastNotificationContainerRef?.current);

    if (data.email_address !== user?.email_address) {
      getNewToken();
      Toast.success('App will now refresh to update.', undefined, toastNotificationContainerRef?.current);
    }

    if (onSuccessSubmitted) {
      onSuccessSubmitted();
    }

    if (onSaveSuccess) {
      onSaveSuccess();
    }
  };

  // Use a ref to store the latest submit function to avoid stale closures
  const submitRef = useRef(submit);
  submitRef.current = submit;

  // Create a stable save handler that can be called from the parent component
  const handleSave = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      handleSubmit(
        async (data) => {
          try {
            await submitRef.current(data);
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        (errors) => {
          // Form validation errors
          reject(new Error('Please fix the form errors before saving'));
        },
      )();
    });
  }, [handleSubmit]);

  // Expose the save handler to parent component for unsaved changes modal
  // This runs once on mount and whenever handleSave changes
  useEffect(() => {
    if (onSaveRequestReady) {
      onSaveRequestReady(handleSave);
    }
  }, [onSaveRequestReady, handleSave]);

  // submit() rejects so the parent's save handler can react; the in-form Save
  // button surfaces those failures inline, so swallow them here rather than
  // letting them escape as an unhandled rejection.
  const handleSaveFromForm = useCallback(() => {
    handleSave().catch(() => {});
  }, [handleSave]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InlineSpinner />
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleSaveFromForm();
      }}
      className="p-6"
    >
      <div ref={toastNotificationContainerRef} />
      <PageHeader
        header="My Profile"
        buttonText={isSubmitting ? 'Saving...' : 'Save'}
        isButtonDisabled={!isDirty || isSubmitting}
        onButtonClick={handleSaveFromForm}
        buttonLoading={isSubmitting}
      />
      {showUnsavedChangesModal && onCloseUnsavedChangesModal && (
        <UnsavedChangesModal
          open={showUnsavedChangesModal}
          onClose={onCloseUnsavedChangesModal}
          onDiscard={onDiscardUnsavedChangesModal}
          savingInProgress={isSubmitting}
        />
      )}
      <div className="space-y-6">
        <FormBlock header="Personal Details">
          <div className="space-y-4">
            <Input
              label="FIRST NAME"
              type="text"
              placeholder="First Name"
              required={true}
              {...register('first_name')}
              onIconClick={() => {}}
              id="first_name"
              error={Boolean(errors?.first_name?.message)}
            />
            {errors?.first_name?.message && <FormError text={errors?.first_name?.message as string} />}
            <Input
              label="LAST NAME"
              type="text"
              placeholder="Last Name"
              required={true}
              {...register('last_name')}
              onIconClick={() => {}}
              id="last_name"
              error={Boolean(errors?.last_name?.message)}
            />
            {errors?.last_name?.message && <FormError text={errors?.last_name?.message as string} />}
            <div>
              <FormItemName name="DATE OF BIRTH" isRequired className="mb-[6px]" />
              {user && (
                <Controller
                  name="date_of_birth"
                  control={control}
                  defaultValue={
                    user?.date_of_birth === '0001-01-01'
                      ? ''
                      : moment(user?.date_of_birth ?? '')
                          ?.format('MM-DD-yyyy')
                          .toString()
                  }
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <DatePicker
                      value={value?.replaceAll('-', '/')}
                      onChange={(value: any | null) => {
                        if (value) {
                          const date =
                            +value?.format('yyyy') < 1000 || !value?.year()
                              ? INVALID_DATE
                              : value?.format('MM-DD-yyyy').toString();
                          return onChange(date);
                        } else {
                          setValue('date_of_birth', '', {
                            shouldDirty: true,
                          });
                        }
                      }}
                      onOpen={() => {
                        if (value === '') {
                          return onChange('01-01-1990');
                        }
                      }}
                    />
                  )}
                />
              )}
              {errors?.date_of_birth?.message && (
                <FormError text={errors?.date_of_birth?.message as string} className="mt-3" />
              )}
            </div>
          </div>
        </FormBlock>
        <FormBlock header="Contact Details">
          <div className="space-y-4">
            <div>
              <label htmlFor="mobile_phone" className="text-primary font-open-sans mb-2 block text-sm font-semibold">
                PHONE NUMBER
              </label>
              <input
                id="mobile_phone"
                type="tel"
                value={hasChangedPhoneNumber ? phoneNumber : normalizePhoneNumber(getValues('mobile_phone') || '')}
                onChange={handlePhoneChange}
                placeholder="(123) 456-7890"
                className="focus:ring-primary font-open-sans border-avatar-border-slate h-12 w-full rounded-md border px-3 focus:border-transparent focus:outline-none focus:ring-2"
                maxLength={14}
              />
              {phoneValidationError && <p className="text-red font-open-sans mt-1 text-sm">{phoneValidationError}</p>}
              {errors?.mobile_phone?.message && <FormError text={errors?.mobile_phone?.message as string} />}
            </div>

            {(hasPendingPhoneChange || showPhoneValidation) && (
              <div className="space-y-4">
                {!showPhoneValidation && (
                  <button
                    type="button"
                    onClick={handlePhoneValidation}
                    className="text-primary font-open-sans bg-button-secondary hover:bg-button-secondary-hover w-full rounded-full px-4 py-2.5 text-base font-medium disabled:opacity-50"
                    disabled={isPhoneValidating || !phoneNumber || phoneNumber.replace(/\D/g, '').length !== 10}
                  >
                    {isPhoneValidating ? 'Sending...' : 'Verify Phone Number'}
                  </button>
                )}

                {showPhoneValidation && (
                  <div className="border-avatar-border-slate bg-button-secondary rounded-md border p-4">
                    <OtpVerification
                      value={otpValue}
                      onChange={(value) => {
                        setOtpValue(value);
                        if (otpValidationError) {
                          setOtpValidationError(null);
                        }
                      }}
                      onSubmit={handleOtpSubmit}
                      onResend={resendVerificationCode}
                      onCancel={discardPhoneChange}
                      isSubmitting={isPhoneValidating}
                      isResending={isPhoneValidating}
                      error={otpValidationError}
                      phoneNumber={phoneNumber}
                      showCancelButton={true}
                      submitButtonText="Verify"
                      cancelButtonText="Cancel"
                      submitButtonClassName="flex-1 rounded-md px-3 py-2 text-sm text-white hover:opacity-80 disabled:opacity-50 bg-primary"
                      containerStyle="flex justify-center"
                    />
                  </div>
                )}
              </div>
            )}

            <Input
              label="EMAIL ADDRESS"
              type="email"
              required
              placeholder="Your email"
              {...register('email_address')}
              id="email_address"
              onIconClick={() => {}}
              error={Boolean(errors?.email_address?.message)}
              disabled={!user?.caller_role?.active_subscription?.package?.client.member_self_enrollment}
            />
            {errors?.email_address?.message && <FormError text={errors?.email_address?.message as string} />}
          </div>
        </FormBlock>
        <FormBlock header="Receive texts">
          <div className="flex flex-col items-start justify-start gap-5">
            <div className="inline-flex items-start justify-start gap-4">
              <RadioButton
                data-testid="toggle-phone-radio"
                variant={'DEFAULT'}
                checked={watch('is_text_compatible_phone') === true}
                onChange={(checked) => {
                  setValue('is_text_compatible_phone', true, { shouldDirty: true });
                  if (onChange) onChange();
                }}
              />
              <div className="font-open-sans shrink grow basis-0 text-base font-normal leading-normal text-black">
                Yes, send me texts from {theme.name}
              </div>
            </div>
            <div className="inline-flex items-start justify-start gap-4">
              <RadioButton
                variant={'DEFAULT'}
                checked={watch('is_text_compatible_phone') === false}
                onChange={(checked) => {
                  setValue('is_text_compatible_phone', false, { shouldDirty: true });
                  if (onChange) onChange();
                }}
              />
              <div className="font-open-sans shrink grow basis-0 text-base font-normal leading-normal text-black">
                No
              </div>
            </div>
          </div>
        </FormBlock>
        <FormBlock header="Location">
          <div className="space-y-4">
            <Input
              className="col-span-2"
              label="ADDRESS"
              type="text"
              placeholder="Address"
              {...register('street_address_1')}
              id="street_address_1"
              onIconClick={() => {}}
              error={Boolean(errors?.street_address_1?.message)}
            />
            {errors?.street_address_1?.message && <FormError text={errors?.street_address_1?.message as string} />}
            <Input
              className="col-span-2"
              label="ADDRESS 2"
              type="text"
              placeholder="Address 2"
              {...register('street_address_2')}
              id="street_address_2"
              onIconClick={() => {}}
              error={Boolean(errors?.street_address_2?.message)}
            />
            {errors?.street_address_2?.message && <FormError text={errors?.street_address_2?.message as string} />}
            <Input
              label="CITY"
              type="text"
              placeholder="City"
              {...register('city')}
              id="city"
              onIconClick={() => {}}
              error={Boolean(errors?.city?.message)}
            />
            {errors?.city?.message && <FormError text={errors?.city?.message as string} />}
            <div>
              <Select
                {...register('state')}
                label="STATE"
                className={errors?.state?.message && 'border-red border'}
                options={userStateOptions ?? []}
              />
              {errors?.state?.message && <FormError text={errors?.state?.message as string} className="mt-3" />}
            </div>
            <Input
              label="ZIP CODE"
              type="number"
              placeholder="Zip Code"
              {...register('zip_code')}
              id="zip_code"
              onIconClick={() => {}}
              error={Boolean(errors?.zip_code?.message)}
            />
            {errors?.zip_code?.message && <FormError text={errors?.zip_code?.message as string} />}
            <div>
              <Select
                {...register('timezone')}
                label="TIME ZONE"
                isRequired
                className={errors?.timezone?.message && 'border-red border'}
                options={supportedTimezonesOptions ?? []}
              />
              {errors?.timezone?.message && <FormError text={errors?.timezone?.message as string} className="mt-3" />}
            </div>
          </div>
        </FormBlock>
        <FormBlock header="Identity">
          <div className="space-y-4">
            <div className="col-span-2">
              <Select
                {...register('gender')}
                label="Gender"
                isRequired
                className={errors?.gender?.message && 'border-red border'}
                options={genderOptionsList ?? []}
              />
              {errors?.gender?.message && <FormError text={errors?.gender?.message as string} className="mt-3" />}
            </div>
            <div className="col-span-2">
              <Select
                {...register('pronoun')}
                label="Pronouns"
                isRequired
                className={errors?.pronoun?.message && 'border-red border'}
                options={pronounOptionsList ?? []}
              />
              {errors?.pronoun?.message && <FormError text={errors?.pronoun?.message as string} className="mt-3" />}
            </div>
          </div>
        </FormBlock>
      </div>
    </form>
  );

  function getFormOptions(key: string, formDataOptions: TagGroup[] | undefined = []) {
    return formDataOptions
      ?.find((item: any) => item.key === key)
      ?.tags?.map(({ name }) => ({
        value: name,
        label: name,
        name,
      }))
      .concat({
        value: preferNotToSay,
        label: preferNotToSay,
        name: preferNotToSay,
      });
  }
};
